import { z } from 'zod';
import { commissionDealsSchema } from '../pricing/commission-rates';
import { isRetiredCompanyType } from './contact';
import { importConflictsSchema, importStatusSchema } from './import-status';
import { productAttachmentSchema } from './products';

// The first four are verbatim from legacy
// `travel_planner_back/src/crm/enums/company-status.ts`; 'Archived' is the
// supplier record's addition (apps/crm/prd/crm-supplier-record.md §4).
// `company_status` is a plain text column, so this needs no migration.
export const companyStatusValues = [
  'Active',
  'Inactive',
  'Blacklisted',
  'Deleted',
  'Archived',
] as const;
export const companyStatusSchema = z.enum(companyStatusValues);
export type CompanyStatus = z.infer<typeof companyStatusSchema>;

// The first three are verbatim from legacy
// `travel_planner_back/src/crm/enums/company-supplier-type.ts` and are KEPT
// because the Accounts list filter and crm.v_supplier depend on them; the rest
// are the supplier record's controlled list (spec §4). 'Guide' is shared.
export const companySupplierTypeValues = [
  'Transport',
  'Ticket',
  'Guide',
  'Hotel',
  'DMC',
  'Activity Provider',
  'Restaurant',
  'Transport Provider',
  'Venue',
  'Other',
] as const;
export const companySupplierTypeSchema = z.enum(companySupplierTypeValues);
export type CompanySupplierType = z.infer<typeof companySupplierTypeSchema>;

// Statuses a supplier may be picked under for a NEW product (spec §4). An
// Inactive or Archived supplier stays readable and stays attached to the
// products that already reference it — it is only kept out of the picker's
// candidate list. A missing status is treated as selectable (legacy rows).
export const companySelectableStatusValues = ['Active'] as const;

export const isSelectableSupplierStatus = (
  status: string | null | undefined
): boolean =>
  status == null ||
  (companySelectableStatusValues as readonly string[]).includes(status);

// ─── Supplier attachments (spec §7/§8) ─────────────────────────────
//
// Stored on `crm.company.attachments` (jsonb) using the SAME shape as
// `product.attachments` / `itinerary_item.attachments`, so the UploadThing
// pointers and the existing round-trip helpers work unchanged. The product
// fields already carry most of the spec's metadata — reused, not duplicated:
//
//   fileKey          -> storage_key          caption -> display_name
//   file.name        -> original_filename    order   -> display order
//   file.size        -> file_size_bytes      type    -> always 'DOCUMENT'
//   file.contentType -> mime_type
//
// Only genuinely new metadata gets new keys, below.
export const companyAttachmentCategoryValues = [
  'Contract',
  'Rate Sheet',
  'Fact Sheet',
  'Menu',
  'Insurance',
  'Operational Manual',
  'Presentation',
  'Other',
] as const;
export const companyAttachmentCategorySchema = z.enum(
  companyAttachmentCategoryValues
);
export type CompanyAttachmentCategory = z.infer<
  typeof companyAttachmentCategorySchema
>;

// Neither value makes a file customer-facing. 'Internal' is CRM-only;
// 'ProductAvailable' may additionally be surfaced to authorised users working
// on a linked product (spec §7).
export const companyAttachmentVisibilityValues = [
  'Internal',
  'ProductAvailable',
] as const;
export const companyAttachmentVisibilitySchema = z.enum(
  companyAttachmentVisibilityValues
);
export type CompanyAttachmentVisibility = z.infer<
  typeof companyAttachmentVisibilitySchema
>;

export const companyAttachmentSchema = productAttachmentSchema.extend({
  // Groups every version of one logical document. Replacing a file appends a
  // new row sharing this id rather than overwriting the old one (spec §8).
  documentId: z.string(),
  // 1-based, unique within a documentId. Replace => previous + 1.
  version: z.number().int().positive(),
  // Exactly one true row per documentId; superseded rows are false. Lists show
  // the true row only, and products always resolve it (no version pinning).
  isCurrent: z.boolean(),
  category: companyAttachmentCategorySchema,
  visibility: companyAttachmentVisibilitySchema,
  validFrom: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  contractYear: z.number().int().nullable().optional(),
  // Pins a document to the top of the supplier's list and of the product
  // preview's supplier section. Optional because every row written before the
  // feature existed lacks the key — treat a missing value as `false` rather
  // than backfilling the jsonb (see `isFeatured`).
  featured: z.boolean().optional(),
  uploadedAt: z.string(),
  uploadedBy: z.uuid().nullable().optional(),
});

export type CompanyAttachment = z.infer<typeof companyAttachmentSchema>;

/**
 * XT-903 — `company_origin` of an agency a portal agent created for themselves.
 * Such a company never connects an AI assistant as a company login.
 */
export const PORTAL_CREATED_COMPANY_ORIGIN = 'agent_portal';

export const companySchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  companyType: z.string().nullable(),
  companyIdNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  streetAddress: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  postalCode: z.string().nullable(),
  officePhone: z.string().nullable(),
  email: z.string().nullable(),
  billingCountry: z.string().nullable(),
  billingCity: z.string().nullable(),
  billingStreetAddress: z.string().nullable(),
  billingStateOrRegion: z.string().nullable(),
  billingPostalCode: z.string().nullable(),
  billingOfficePhone: z.string().nullable(),
  billingEmail: z.string().nullable(),
  description: z.string().nullable(),
  companyStatus: z.string().nullable(),
  notes: z.string().nullable(),
  website: z.string().nullable(),
  leadSource: z.array(z.string()).nullable(),
  label: z.array(z.string()).nullable(),
  useItineraryDomain: z.boolean().nullable(),
  organizationId: z.uuid(),
  externalId: z.string().nullable(),
  supplierType: companySupplierTypeSchema.nullable(),
  showBudgetBreakdownInPreviewDefault: z.boolean().nullable(),
  importStatus: importStatusSchema.nullable(),
  importConflicts: importConflictsSchema,
  // Supplier record. NOTE: `mapCompany` in companies.service.ts enumerates
  // every field explicitly and ends in `companySchema.parse`, so these three
  // must be added there too — schema-only throws on every company read,
  // map-only drops the field silently.
  version: z.number().int(),
  contractYear: z.number().int().nullable(),
  attachments: z.array(companyAttachmentSchema).nullable(),
  // XT-903 — a consultant blocked this company login from connecting an AI
  // assistant (MCP). `companyOrigin` is 'agent_portal' for an agency a portal
  // agent created, which never connects as a company login.
  aiBlocked: z.boolean(),
  companyOrigin: z.string().nullable(),
  // XT-368 — "Domain signup allowed for agent portal": an unknown email at
  // this company's main-email domain may sign in, as a new contact under it.
  portalDomainSignup: z.boolean(),
  // XT-964 — the agency's special commission deal, a rate per category.
  commissionDeals: commissionDealsSchema,
  // Agency logo (uploadthing key) shown on the traveller preview. Written only
  // by `companies.setLogo` and the agent portal, never by create/update.
  logoKey: z.string().nullable().optional(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  deletedOn: z.string().nullable(),
});

export type Company = z.infer<typeof companySchema>;

// Retired types (XT-892) cannot be written; reading them is still allowed.
const writableCompanyType = z
  .string()
  .refine((value) => !isRetiredCompanyType(value), {
    message: 'This account type is no longer available',
  });

export const createCompanyInput = z.object({
  name: z.string().optional(),
  companyType: writableCompanyType.optional(),
  companyIdNumber: z.string().optional(),
  vatId: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  streetAddress: z.string().optional(),
  stateOrRegion: z.string().optional(),
  postalCode: z.string().optional(),
  officePhone: z.string().optional(),
  email: z.string().optional(),
  billingCountry: z.string().optional(),
  billingCity: z.string().optional(),
  billingStreetAddress: z.string().optional(),
  billingStateOrRegion: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingOfficePhone: z.string().optional(),
  billingEmail: z.string().optional(),
  description: z.string().optional(),
  companyStatus: z.string().optional(),
  notes: z.string().optional(),
  website: z.string().optional(),
  leadSource: z.array(z.string()).optional(),
  label: z.array(z.string()).optional(),
  useItineraryDomain: z.boolean().optional(),
  externalId: z.string().optional(),
  supplierType: companySupplierTypeSchema.optional(),
  showBudgetBreakdownInPreviewDefault: z.boolean().optional(),
  // Year of the latest valid commercial agreement / rate set (spec §4). Does
  // not replace per-attachment validity dates. `attachments` and `version` are
  // deliberately absent: attachments save through their own mutation and the
  // version counter is server-owned.
  contractYear: z.number().int().optional(),
  aiBlocked: z.boolean().optional(),
  portalDomainSignup: z.boolean().optional(),
  commissionDeals: commissionDealsSchema.optional(),
});

export type CreateCompanyInput = z.infer<typeof createCompanyInput>;

export const updateCompanyInput = z.object({
  id: z.uuid(),
  name: z.string().optional(),
  companyType: writableCompanyType.optional(),
  companyIdNumber: z.string().optional(),
  vatId: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  streetAddress: z.string().optional(),
  stateOrRegion: z.string().optional(),
  postalCode: z.string().optional(),
  officePhone: z.string().optional(),
  email: z.string().optional(),
  billingCountry: z.string().optional(),
  billingCity: z.string().optional(),
  billingStreetAddress: z.string().optional(),
  billingStateOrRegion: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingOfficePhone: z.string().optional(),
  billingEmail: z.string().optional(),
  description: z.string().optional(),
  companyStatus: z.string().optional(),
  notes: z.string().optional(),
  website: z.string().optional(),
  leadSource: z.array(z.string()).optional(),
  label: z.array(z.string()).optional(),
  useItineraryDomain: z.boolean().optional(),
  externalId: z.string().optional(),
  // `null` clears the supplier type — used by the form's "Clear" option,
  // mirroring legacy `AccountsFormSchema` `'CLEAR'` sentinel.
  supplierType: companySupplierTypeSchema.nullable().optional(),
  showBudgetBreakdownInPreviewDefault: z.boolean().optional(),
  contractYear: z.number().int().nullable().optional(),
  aiBlocked: z.boolean().optional(),
  portalDomainSignup: z.boolean().optional(),
  commissionDeals: commissionDealsSchema.optional(),
  // Optimistic concurrency (spec §13). When supplied, the UPDATE additionally
  // requires `version = expectedVersion` and the router raises CONFLICT if no
  // row matched. OMITTING it is deliberate last-write-wins — excelImport and
  // other bulk callers rely on that, so never make this required.
  expectedVersion: z.number().int().positive().optional(),
});

export type UpdateCompanyInput = z.infer<typeof updateCompanyInput>;

// Attachments save through their own action, never the Info tab's Save button
// (spec §12). The client owns the version chain and sends the whole array;
// `action` + `documentId` only feed the audit entry (spec §18).
export const updateCompanyAttachmentsInput = z.object({
  id: z.uuid(),
  attachments: z.array(companyAttachmentSchema),
  // `categorised` is a metadata-only edit (category / visibility / validity) —
  // distinct from `replaced`, which appends a new binary version. Keeping them
  // apart matters for the audit trail and for the toast wording.
  //
  // `renamed` and `featured` are metadata-only too, but stay distinct from
  // `categorised` so the activity log says what actually changed and can carry
  // the previous/new display name (Melhoria 5 §12).
  action: z.enum([
    'added',
    'replaced',
    'removed',
    'categorised',
    'renamed',
    'featured',
  ]),
  documentId: z.string(),
});

export type UpdateCompanyAttachmentsInput = z.infer<
  typeof updateCompanyAttachmentsInput
>;

// Melhoria 5 §4/§11: opening a document goes through the server so access is
// authorised per request and the returned URL is short-lived. The permanent
// storage URL is deliberately NOT the thing the client navigates to.
export const companyAttachmentOpenUrlInput = z.object({
  id: z.uuid(),
  documentId: z.string(),
  // Which version to open. Omitted means the current one; the version-history
  // rows pass an explicit version so a superseded file stays reachable.
  version: z.number().int().positive().optional(),
});

export type CompanyAttachmentOpenUrlInput = z.infer<
  typeof companyAttachmentOpenUrlInput
>;

export const companyAttachmentOpenUrlResult = z.object({
  url: z.string(),
  expiresAt: z.string(),
  fileName: z.string(),
});

export type CompanyAttachmentOpenUrlResult = z.infer<
  typeof companyAttachmentOpenUrlResult
>;

export const companySortFields = [
  'name',
  'email',
  'phone',
  'country',
  'status',
] as const;
export type CompanySortField = (typeof companySortFields)[number];

// Filter shape mirrors legacy `FindCompanyDto`:
// multi-select on companyType[] / supplierType[] / organizationIds[],
// plus `name` and `email` substring searches. Pagination via offset/limit.
export const findCompaniesInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(50),
  name: z.string().optional(),
  // Independent of `name` — the two AND together, so the Accounts list can be
  // narrowed by either or both. Matched against the same `email` column the
  // list's Email column renders and sorts on (never `billingEmail`), so a
  // result always shows the address that matched (XT-175 / TCC02-011).
  email: z.string().optional(),
  companyType: z.array(z.string()).optional(),
  supplierType: z.array(companySupplierTypeSchema).optional(),
  organizationIds: z.array(z.uuid()).optional(),
  // Accounts carrying ANY of these labels (XT-892).
  label: z.array(z.string().min(1)).max(50).optional(),
  sortBy: z.enum(companySortFields).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type FindCompaniesInput = z.infer<typeof findCompaniesInput>;

// companies.labels — the distinct labels on the accounts the list can show,
// searched on the server (there are thousands), most used first.
export const findCompanyLabelsInput = z.object({
  search: z.string().optional(),
  organizationIds: z.array(z.uuid()).optional(),
  limit: z.number().int().positive().max(100).default(50),
});
export type FindCompanyLabelsInput = z.infer<typeof findCompanyLabelsInput>;

export const companyLabelOptionSchema = z.object({
  label: z.string(),
  count: z.number().int().nonnegative(),
});
export type CompanyLabelOption = z.infer<typeof companyLabelOptionSchema>;

// Bulk id → name resolver used by the itinerary planner subitem rows
// (and any other consumer that needs to turn a known set of supplier
// UUIDs into display names). Caller hands in the exact UUIDs it needs
// — no global cap, no `crm:read` scope creep.
export const resolveCompaniesByIdsInput = z.object({
  ids: z.array(z.uuid()).max(500),
});

export type ResolveCompaniesByIdsInput = z.infer<
  typeof resolveCompaniesByIdsInput
>;

// Name typeahead for the planner's supplier suggester. Same shape as
// `resolveCompaniesByIdsInput`: `{ id, name }[]` only, gated by
// `itineraries:read` (not `crm:read`). Empty `name` returns the first
// 50 suppliers — legacy parity (`useGetSuppliers({ limit: 50 })` on
// popover open before the user types).
export const searchCompaniesForItinerariesInput = z.object({
  name: z.string().max(200),
});

export type SearchCompaniesForItinerariesInput = z.infer<
  typeof searchCompaniesForItinerariesInput
>;

// ─── Excel import ──────────────────────────────────────────────────

export const COMPANY_EXCEL_IMPORT_ROW_CAP = 5000;

// Cap the base64 payload at ~13.4 MB raw (≈ 10 MB decoded). Plenty of
// headroom for a 5000-row workbook (typical .xlsx is < 1 MB), bounded
// enough to keep request parsing predictable.
const MAX_BASE64_LENGTH = 13_400_000;

export const companyExcelImportInput = z.object({
  // Base64-encoded .xlsx payload. The mutation decodes this with
  // exceljs; total decoded byte length is bounded by MAX_BASE64_LENGTH
  // here in addition to the upstream HTTP body limit. Keep `mode`
  // opt-in so callers must pick.
  base64: z.string().min(1).max(MAX_BASE64_LENGTH),
  mode: z.enum(['create', 'upsert']).default('create'),
});
export type CompanyExcelImportInput = z.infer<typeof companyExcelImportInput>;

export const companyExcelImportRowErrorSchema = z.object({
  // Excel row number (1-based — matches what users see). `0` is a
  // sentinel for workbook-level errors that don't tie to a row (e.g.
  // "no worksheets").
  row: z.number().int().nonnegative(),
  column: z.number().int().positive().optional(),
  field: z.string().optional(),
  message: z.string(),
  value: z.unknown().optional(),
});
export type CompanyExcelImportRowError = z.infer<
  typeof companyExcelImportRowErrorSchema
>;

export const companyExcelImportResultSchema = z.object({
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(companyExcelImportRowErrorSchema),
});
export type CompanyExcelImportResult = z.infer<
  typeof companyExcelImportResultSchema
>;

// ─── Excel export ──────────────────────────────────────────────────
//
// Counterpart to `excelImport`. Exports the WHOLE filtered result set (not
// the page the user is looking at), so the input reuses `findCompaniesInput`
// minus its pagination — the service pins offset/limit itself.
//
// Hard cap so a runaway export can't OOM the API process: ExcelJS holds the
// workbook in memory before serialising. Over the cap the service raises
// PRECONDITION_FAILED telling the user to narrow filters, rather than
// silently truncating (a half file that looks complete is worse than none).
export const COMPANY_EXCEL_EXPORT_ROW_CAP = 10_000;

export const companyExcelExportInput = findCompaniesInput.omit({
  offset: true,
  limit: true,
});

export type CompanyExcelExportInput = z.infer<typeof companyExcelExportInput>;

export const companyExcelExportResultSchema = z.object({
  base64: z.string(),
  filename: z.string(),
  mime: z.literal(
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ),
});

export type CompanyExcelExportResult = z.infer<
  typeof companyExcelExportResultSchema
>;
